<template>
  <div
    v-if="show"
    class="fixed z-[9999] inset-0 bg-slate-950/70 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
  >
    <div
      class="bg-white dark:bg-[#111C30] w-full sm:max-w-3xl max-h-screen sm:max-h-[92vh] sm:rounded-2xl border-0 sm:border border-slate-200/90 dark:border-[#263449] relative animate-fade-in flex flex-col shadow-2xl overflow-hidden"
    >
      <!-- Modal Loading Overlay -->
      <AppLoader
        :isVisible="isLoadingClasses || isPromoting || isLoadingPreview"
        variant="card"
        size="md"
        :message="
          isPromoting
            ? 'Promoting Students & Carrying Forward Fees...'
            : isLoadingPreview
            ? 'Calculating Previous Dues & Financial Breakdown...'
            : 'Loading Classes...'
        "
        :subtext="
          isPromoting
            ? 'Preserving historical ledger and applying carry forward balances'
            : isLoadingPreview
            ? 'Inspecting previous session fees, admission dues, and monthly records'
            : 'Retrieving available grade levels'
        "
        color="emerald"
      />

      <!-- Fixed Header -->
      <div
        class="flex-shrink-0 px-5 sm:px-7 py-4 border-b border-slate-100 dark:border-[#263449] bg-gradient-to-r from-emerald-950 via-slate-900 to-slate-900 text-white flex items-center justify-between"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 shadow-xs"
          >
            <TrendingUp class="w-5 h-5" />
          </div>
          <div>
            <h2 class="text-base sm:text-lg font-black tracking-tight text-white">
              Smart Student Promotion & Fee Carry Forward
            </h2>
            <p class="text-xs text-emerald-300/80 font-medium">
              Promote {{ effectiveStudentsCount }} student{{
                effectiveStudentsCount > 1 ? "s" : ""
              }} • Automatic arrears carry forward & historical preservation
            </p>
          </div>
        </div>
        <button
          @click="$emit('close')"
          :disabled="isLoadingClasses || isPromoting || isLoadingPreview"
          class="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
          title="Close Modal"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 bg-slate-50/60 dark:bg-[#0B1120] space-y-4 sm:space-y-5">
        <!-- Error Banner -->
        <div
          v-if="errorMessage"
          class="p-3.5 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/50 rounded-xl text-rose-700 dark:text-rose-300 text-xs font-semibold flex items-center gap-2"
        >
          <AlertCircle class="w-4 h-4 text-rose-500 shrink-0" />
          <span>{{ errorMessage }}</span>
        </div>

        <!-- 1. Selection Inputs (Target Class & Session) -->
        <div class="bg-white dark:bg-[#111C30] p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-[#A6B4C8]">
            Promotion Target Configuration
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <!-- Select Target Class -->
            <div class="space-y-1.5">
              <label class="block text-xs font-bold text-slate-700 dark:text-[#F1F5F9]">
                Target Class <span class="text-rose-500">*</span>
              </label>
              <select
                v-model="selectedClassId"
                @change="handleClassOrSessionChange"
                :disabled="isLoadingClasses || isPromoting"
                class="w-full rounded-xl border border-slate-300 dark:border-[#263449] px-3.5 py-2.5 bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] text-xs sm:text-sm font-semibold focus:ring-2 focus:ring-emerald-500 outline-none transition cursor-pointer"
              >
                <option value="" disabled>-- Select Destination Class --</option>
                <option
                  v-for="cls in availableClasses"
                  :key="cls._id"
                  :value="cls._id"
                >
                  {{ cls.name }}
                  <span v-if="cls.section"> - {{ cls.section }}</span>
                </option>
              </select>
            </div>

            <!-- Select / Enter Academic Session -->
            <div class="space-y-1.5">
              <label class="block text-xs font-bold text-slate-700 dark:text-[#F1F5F9]">
                New Academic Session <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="session"
                @blur="handleClassOrSessionChange"
                type="text"
                placeholder="e.g. 2024-2025"
                :disabled="isLoadingClasses || isPromoting"
                class="w-full rounded-xl border border-slate-300 dark:border-[#263449] px-3.5 py-2.5 bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] text-xs sm:text-sm font-semibold focus:ring-2 focus:ring-emerald-500 outline-none transition"
              />
            </div>
          </div>

          <!-- Passout Extra Fields -->
          <div
            v-if="selectedClass?.name?.toLowerCase() === 'passout'"
            class="pt-3 border-t border-slate-100 dark:border-[#263449] grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Passout Year
              </label>
              <input
                v-model="year"
                type="number"
                placeholder="e.g. 2025"
                class="w-full rounded-xl border border-slate-300 dark:border-[#263449] px-3 py-2 text-xs bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9]"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Remarks / Reference
              </label>
              <input
                v-model="remarks"
                type="text"
                placeholder="Optional transfer remarks"
                class="w-full rounded-xl border border-slate-300 dark:border-[#263449] px-3 py-2 text-xs bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9]"
              />
            </div>
          </div>
        </div>

        <!-- 2. Financial Breakdown & Carry Forward Preview -->
        <div v-if="previewData && selectedClass?.name?.toLowerCase() !== 'passout'" class="space-y-4">
          <!-- Summary Cards -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <!-- 1. Previous Class Outstanding (Arrears) -->
            <div
              class="p-4 rounded-2xl border transition-all"
              :class="
                (previewData.summary?.grandPreviousDues || 0) > 0
                  ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800/50'
                  : 'bg-white dark:bg-[#111C30] border-slate-200/80 dark:border-[#263449]'
              "
            >
              <div class="flex items-center justify-between text-amber-700 dark:text-amber-400 mb-1">
                <span class="text-[10px] font-bold uppercase tracking-wider">Previous Class Outstanding</span>
                <History class="w-3.5 h-3.5" />
              </div>
              <div class="text-lg sm:text-xl font-black font-mono text-amber-900 dark:text-amber-200">
                ₹{{ (previewData.summary?.grandPreviousDues || 0).toLocaleString("en-IN") }}
              </div>
              <span class="text-[10px] text-amber-700 dark:text-amber-400 font-semibold block mt-0.5">
                {{ (previewData.summary?.grandPreviousDues || 0) > 0 ? "Carried forward as arrears" : "✓ No previous dues" }}
              </span>
            </div>

            <!-- 2. Target Class Fees -->
            <div class="bg-white dark:bg-[#111C30] p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449]">
              <div class="flex items-center justify-between text-indigo-600 dark:text-indigo-400 mb-1">
                <span class="text-[10px] font-bold uppercase tracking-wider">New Class Fee Structure</span>
                <GraduationCap class="w-3.5 h-3.5" />
              </div>
              <div class="text-lg sm:text-xl font-black font-mono text-slate-900 dark:text-[#F1F5F9]">
                ₹{{ (previewData.summary?.grandNewClassFees || 0).toLocaleString("en-IN") }}
              </div>
              <span class="text-[10px] text-slate-500 dark:text-[#A6B4C8] font-medium block mt-0.5">
                Admission: ₹{{ (previewData.targetClass?.admissionFee || 0).toLocaleString("en-IN") }} • ₹{{ (previewData.targetClass?.monthlyFee || 0).toLocaleString("en-IN") }}/mo
              </span>
            </div>

            <!-- 3. Grand Total Initial Balance -->
            <div class="bg-slate-900 text-white p-4 rounded-2xl border border-slate-800 shadow-md">
              <div class="flex items-center justify-between text-emerald-400 mb-1">
                <span class="text-[10px] font-bold uppercase tracking-wider">Initial Outstanding</span>
                <CreditCard class="w-3.5 h-3.5" />
              </div>
              <div class="text-lg sm:text-xl font-black font-mono text-emerald-400">
                ₹{{ (previewData.summary?.grandTotalInitialBalance || 0).toLocaleString("en-IN") }}
              </div>
              <span class="text-[10px] text-slate-300 block mt-0.5">
                Arrears + New Admission Due
              </span>
            </div>
          </div>

          <!-- Carry-Forward Guarantee Banner -->
          <div
            class="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 p-3.5 rounded-2xl text-xs flex items-start gap-3"
          >
            <ShieldCheck class="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <div class="text-blue-900 dark:text-blue-200 text-[11px] leading-relaxed">
              <strong>Immutable Audit Guarantee:</strong> Promoting students preserves all historical fee records intact. Unpaid admission fees and unpaid tuition months are carried forward with their original class and academic session IDs. Historical receipts and transactions remain untouched.
            </div>
          </div>

          <!-- Student-wise Detailed Breakdown Table -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] overflow-hidden shadow-xs">
            <div class="px-4 py-3 bg-slate-50 dark:bg-[#18243A] border-b border-slate-200/80 dark:border-[#263449] flex items-center justify-between">
              <span class="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-[#A6B4C8]">
                Student Financial Breakdown ({{ previewData.students?.length || 0 }})
              </span>
              <span class="text-[11px] text-slate-500 dark:text-[#7E8DA5] font-medium">
                Target: {{ previewData.targetClass?.name }}
              </span>
            </div>

            <div class="divide-y divide-slate-100 dark:divide-[#263449] max-h-72 overflow-y-auto">
              <div
                v-for="st in (previewData.students || [])"
                :key="st.studentId"
                class="p-4 hover:bg-slate-50/70 dark:hover:bg-[#172235] transition"
              >
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                  <div class="flex items-center space-x-3">
                    <div class="w-9 h-9 rounded-xl bg-slate-100 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] flex items-center justify-center font-bold text-xs text-indigo-600 dark:text-indigo-400 shrink-0">
                      {{ st.rollNumber || "#" }}
                    </div>
                    <div>
                      <h4 class="text-xs sm:text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">
                        {{ st.studentName }}
                      </h4>
                      <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8]">
                        From: <strong class="text-slate-700 dark:text-slate-300">{{ st.currentClassName }}</strong>
                        <span v-if="st.currentSession"> ({{ st.currentSession }})</span>
                        → To: <strong class="text-emerald-600 dark:text-emerald-400">{{ st.targetClassName }}</strong>
                      </p>
                    </div>
                  </div>

                  <div class="flex items-center gap-3 self-end sm:self-center">
                    <div class="text-right">
                      <span class="text-[10px] text-slate-400 uppercase font-bold block">Previous Arrears</span>
                      <span
                        class="text-xs font-mono font-bold"
                        :class="st.totalPreviousOutstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600'"
                      >
                        ₹{{ (st.totalPreviousOutstanding || 0).toLocaleString("en-IN") }}
                      </span>
                    </div>

                    <div class="text-right">
                      <span class="text-[10px] text-slate-400 uppercase font-bold block">New Initial Due</span>
                      <span class="text-xs font-mono font-black text-slate-900 dark:text-[#F1F5F9]">
                        ₹{{ (st.totalInitialOutstanding || 0).toLocaleString("en-IN") }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Unpaid Items Details Pills -->
                <div
                  v-if="st.totalPreviousOutstanding > 0"
                  class="mt-2.5 pt-2 border-t border-dashed border-slate-200 dark:border-[#263449] flex flex-wrap items-center gap-2 text-[11px]"
                >
                  <span
                    v-if="st.unpaidAdmission > 0"
                    class="px-2 py-0.5 bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 rounded-md font-semibold"
                  >
                    Admission Unpaid: ₹{{ st.unpaidAdmission.toLocaleString("en-IN") }}
                  </span>
                  <span
                    v-if="st.unpaidMonthlyFee > 0"
                    class="px-2 py-0.5 bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 rounded-md font-semibold"
                  >
                    Tuition Due: {{ st.unpaidMonthsCount }} month(s) (₹{{ st.unpaidMonthlyFee.toLocaleString("en-IN") }})
                  </span>
                  <span
                    v-if="st.unpaidAdjustmentsTotal > 0"
                    class="px-2 py-0.5 bg-purple-100 dark:bg-purple-950/60 text-purple-800 dark:text-purple-300 rounded-md font-semibold"
                  >
                    Charges: ₹{{ st.unpaidAdjustmentsTotal.toLocaleString("en-IN") }}
                  </span>
                  <span
                    v-if="st.isAlreadyPromoted"
                    class="px-2 py-0.5 bg-amber-500 text-white rounded-md font-bold text-[10px]"
                  >
                    Already in this class & session
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Action Bar -->
      <div
        class="flex-shrink-0 p-4 sm:p-5 border-t border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#111C30] flex items-center justify-between gap-3"
      >
        <div class="text-xs text-slate-500 dark:text-[#A6B4C8] hidden sm:block font-medium">
          <span v-if="previewData?.summary">
            Total Outstanding Realized: <strong class="text-slate-900 dark:text-[#F1F5F9]">₹{{ (previewData.summary?.grandTotalInitialBalance || 0).toLocaleString("en-IN") }}</strong>
          </span>
        </div>

        <div class="flex items-center gap-2.5 w-full sm:w-auto justify-end">
          <button
            @click="$emit('close')"
            :disabled="isLoadingClasses || isPromoting || isLoadingPreview"
            class="flex-1 sm:flex-initial px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#1f2d47] text-slate-700 dark:text-[#A6B4C8] text-xs sm:text-sm font-bold rounded-xl transition cursor-pointer border border-slate-200 dark:border-[#263449]"
          >
            Cancel
          </button>
          <button
            @click="confirmPromotion"
            :disabled="!selectedClassId || isLoadingClasses || isPromoting || isLoadingPreview"
            class="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-bold rounded-xl shadow-xs transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <AppLoader
              v-if="isPromoting"
              variant="button"
              class="w-3.5 h-3.5 text-white"
            />
            <TrendingUp v-else class="w-4 h-4" />
            <span>{{ isPromoting ? "Promoting..." : "Confirm & Carry Forward Dues" }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, watch } from "vue";
  import { useStore } from "vuex";
  import { TrendingUp, X, AlertCircle, History, GraduationCap, CreditCard, ShieldCheck } from "lucide-vue-next";
  import { previewStudentPromotion } from "@/services/feeService";

  const store = useStore();

  const props = defineProps({
    show: Boolean,
    selectedStudentsCount: Number,
    studentIds: {
      type: Array,
      default: () => [],
    },
    studentId: {
      type: String,
      default: null,
    },
    isPromoting: Boolean,
  });

  const emit = defineEmits(["close", "confirm-promote"]);

  const selectedClassId = ref("");
  const session = ref("");
  const year = ref("");
  const remarks = ref("");
  const isLoadingClasses = ref(false);
  const isLoadingPreview = ref(false);
  const errorMessage = ref("");
  const previewData = ref(null);

  const availableClasses = computed(() => store.getters.allClasses || []);
  const selectedClass = computed(() =>
    availableClasses.value.find((c) => c._id === selectedClassId.value)
  );

  const effectiveStudentIds = computed(() => {
    if (props.studentIds && props.studentIds.length > 0) {
      return props.studentIds;
    }
    if (props.studentId) {
      return [props.studentId];
    }
    return [];
  });

  const effectiveStudentsCount = computed(() => {
    if (props.selectedStudentsCount) return props.selectedStudentsCount;
    return effectiveStudentIds.value.length || 1;
  });

  const fetchClasses = async () => {
    isLoadingClasses.value = true;
    errorMessage.value = "";
    try {
      await store.dispatch("fetchAllClasses");
    } catch (err) {
      console.error("Failed to fetch classes for promotion modal:", err);
      errorMessage.value = "Failed to load classes. Please try again.";
    } finally {
      isLoadingClasses.value = false;
    }
  };

  const handleClassOrSessionChange = async () => {
    if (!selectedClassId.value || effectiveStudentIds.value.length === 0) {
      previewData.value = null;
      return;
    }

    if (selectedClass.value?.name?.toLowerCase() === "passout") {
      previewData.value = null;
      return;
    }

    isLoadingPreview.value = true;
    errorMessage.value = "";
    try {
      const res = await previewStudentPromotion({
        studentIds: effectiveStudentIds.value,
        classId: selectedClassId.value,
        session: session.value || null,
      });

      if (res.data?.success) {
        previewData.value = res.data.data;
        if (!session.value && res.data.data.targetClass?.session) {
          session.value = res.data.data.targetClass.session;
        }
      }
    } catch (err) {
      console.error("previewStudentPromotion error:", err);
      // Non-fatal, admin can still proceed
    } finally {
      isLoadingPreview.value = false;
    }
  };

  const confirmPromotion = () => {
    if (!selectedClassId.value) {
      errorMessage.value = "Please select a target class to promote students to.";
      return;
    }

    emit("confirm-promote", {
      studentIds: effectiveStudentIds.value,
      classId: selectedClassId.value,
      session: session.value || null,
      year: year.value ? Number(year.value) : null,
      remarks: remarks.value || null,
    });
  };

  const resetState = () => {
    selectedClassId.value = "";
    session.value = "";
    year.value = "";
    remarks.value = "";
    errorMessage.value = "";
    previewData.value = null;
  };

  onMounted(() => {
    if (props.show) {
      fetchClasses();
      resetState();
    }
  });

  watch(
    () => props.show,
    (newVal) => {
      if (newVal) {
        fetchClasses();
        resetState();
      }
    }
  );
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.25s ease-out;
  }
</style>
